import { assert, assertNever } from "@glideapps/ts-necessities";
import { areValuesEqual, compareValues } from "../common/components/primitives";
import type { GroundValue, LoadedGroundValue, LoadingValue } from "../common/computation-model/data";
import {
    arrayMap,
    asString,
    getSymbolicRepresentationForGroundValue,
    isLoadingValue,
    isNotEmpty,
    isPrimitive,
    isTruthy,
} from "../common/computation-model/data";
import type { Path, RelativePath } from "../common/computation-model/paths";
import { getSymbolicRepresentationForPath, isRootPath } from "../common/computation-model/paths";
import type { GlideDateTime } from "../date-time/glide-date-time";
import { areSetsOverlapping, isArray, normalizeEmailAddress } from "../support";

export type PathOrGroundValue<T> =
    | { readonly isPath: false; readonly value: LoadedGroundValue }
    | { readonly isPath: true; readonly path: T };

export type UnaryConditionKind = "is-truthy" | "is-empty";
export type BinaryConditionKind =
    | "equals"
    | "contains-string"
    | "is-contained-in-string"
    | "is-less"
    | "is-greater"
    | "is-less-or-equal"
    | "is-greater-or-equal"
    | "is-before"
    | "is-after"
    | "is-on-or-before"
    | "is-on-or-after"
    | "is-within"
    | "matches-email-address";

interface UnaryCondition<T> {
    readonly kind: UnaryConditionKind;
    readonly negate: boolean;
    readonly value: PathOrGroundValue<T>;
}

interface BinaryCondition<T> {
    readonly kind: BinaryConditionKind;
    readonly negate: boolean;
    readonly left: PathOrGroundValue<T>;
    readonly right: PathOrGroundValue<T>;
}

export type Condition<T> = UnaryCondition<T> | BinaryCondition<T>;

function isUnaryCondition<T>(c: Condition<T>): c is UnaryCondition<T> {
    return c.kind === "is-truthy" || c.kind === "is-empty";
}

export function getSymbolicRepresentationForPathOrGroundValue<T>(
    value: PathOrGroundValue<T>,
    getPath: (p: T) => Path
): string {
    if (value.isPath) {
        return getSymbolicRepresentationForPath(getPath(value.path));
    } else {
        return getSymbolicRepresentationForGroundValue(value.value);
    }
}

export interface ConditionValuePath {
    readonly path: Path;
    readonly inHostRow: boolean;
}

export function getSymbolicRepresentationForCondition(c: Condition<ConditionValuePath>): string {
    let symbolic: string;
    if (isUnaryCondition(c)) {
        symbolic = `(${c.kind} ${getSymbolicRepresentationForPathOrGroundValue(c.value, p => p.path)})`;
    } else {
        symbolic = `(${getSymbolicRepresentationForPathOrGroundValue(c.left, p => p.path)} ${
            c.kind
        } ${getSymbolicRepresentationForPathOrGroundValue(c.right, p => p.path)})`;
    }
    if (c.negate) {
        symbolic = "!" + symbolic;
    }
    return symbolic;
}

export function getPathsForCondition<T extends boolean>(
    c: Condition<ConditionValuePath>,
    inHostRow: T
): T extends true ? readonly RelativePath[] : readonly Path[] {
    const paths: Path[] = [];

    function process(p: PathOrGroundValue<ConditionValuePath>) {
        if (!p.isPath) return;
        if (p.path.inHostRow !== inHostRow) return;
        if (inHostRow) {
            assert(!isRootPath(p.path.path));
        }
        paths.push(p.path.path);
    }

    switch (c.kind) {
        case "equals":
        case "contains-string":
        case "is-contained-in-string":
        case "is-less":
        case "is-greater":
        case "is-less-or-equal":
        case "is-greater-or-equal":
        case "is-before":
        case "is-after":
        case "is-on-or-before":
        case "is-on-or-after":
        case "is-within":
        case "matches-email-address":
            process(c.left);
            process(c.right);
            break;
        case "is-truthy":
        case "is-empty":
            process(c.value);
            break;
        default:
            return assertNever(c);
    }

    return paths as any;
}

export function computeCondition<T>(
    c: Condition<T>,
    getValueForPath: (path: T) => GroundValue,
    parseDateTime: (v: LoadedGroundValue) => GlideDateTime | undefined
): boolean | LoadingValue | undefined {
    function get(pov: PathOrGroundValue<T>): GroundValue {
        if (pov.isPath) {
            return getValueForPath(pov.path);
        } else {
            return pov.value;
        }
    }

    function getNormalized(pov: PathOrGroundValue<T>): GroundValue {
        const v = get(pov);
        if (v === "") return undefined;
        return v;
    }

    function makeEmailAddressSet(v: LoadedGroundValue): ReadonlySet<string> {
        if (isArray(v)) {
            return new Set(arrayMap(v, p => normalizeEmailAddress(asString(p))));
        } else {
            return new Set([normalizeEmailAddress(asString(v))]);
        }
    }

    let result: boolean;
    switch (c.kind) {
        case "equals": {
            const left = get(c.left);
            const right = get(c.right);
            if (isLoadingValue(left)) return left;
            if (isLoadingValue(right)) return right;
            if (!isPrimitive(left) || !isPrimitive(right)) {
                result = left === right;
            } else {
                const areEqual = areValuesEqual(left, right);
                if (areEqual === undefined) return undefined;
                result = areEqual;
            }
            break;
        }
        case "contains-string": {
            const leftValue = getNormalized(c.left);
            const rightValue = getNormalized(c.right);
            if (isLoadingValue(leftValue)) return leftValue;
            if (isLoadingValue(rightValue)) return rightValue;
            const left = asString(leftValue).toLowerCase();
            const right = asString(rightValue).toLowerCase();
            result = left.includes(right);
            break;
        }
        case "is-contained-in-string": {
            const leftValue = getNormalized(c.left);
            const rightValue = getNormalized(c.right);
            if (isLoadingValue(leftValue)) return leftValue;
            if (isLoadingValue(rightValue)) return rightValue;
            const left = asString(leftValue).toLowerCase();
            const right = asString(rightValue).toLowerCase();
            result = right.includes(left);
            break;
        }
        case "is-less": {
            const left = get(c.left);
            const right = get(c.right);
            if (isLoadingValue(left)) return left;
            if (isLoadingValue(right)) return right;
            if (!isPrimitive(left) || !isPrimitive(right)) return undefined;
            const r = compareValues(left, right);
            if (r === undefined) return undefined;
            result = r < 0;
            break;
        }
        case "is-greater": {
            const left = get(c.left);
            const right = get(c.right);
            if (isLoadingValue(left)) return left;
            if (isLoadingValue(right)) return right;
            if (!isPrimitive(left) || !isPrimitive(right)) return undefined;
            const r = compareValues(left, right);
            if (r === undefined) return undefined;
            result = r > 0;
            break;
        }
        case "is-less-or-equal": {
            const left = get(c.left);
            const right = get(c.right);
            if (isLoadingValue(left)) return left;
            if (isLoadingValue(right)) return right;
            if (!isPrimitive(left) || !isPrimitive(right)) return undefined;
            const r = compareValues(left, right);
            if (r === undefined) return undefined;
            result = r <= 0;
            break;
        }
        case "is-greater-or-equal": {
            const left = get(c.left);
            const right = get(c.right);
            if (isLoadingValue(left)) return left;
            if (isLoadingValue(right)) return right;
            if (!isPrimitive(left) || !isPrimitive(right)) return undefined;
            const r = compareValues(left, right);
            if (r === undefined) return undefined;
            result = r >= 0;
            break;
        }
        case "is-before": {
            const leftValue = get(c.left);
            const rightValue = get(c.right);
            if (isLoadingValue(leftValue)) return leftValue;
            if (isLoadingValue(rightValue)) return rightValue;
            const left = parseDateTime(leftValue);
            const right = parseDateTime(rightValue);
            if (left === undefined || right === undefined) return undefined;
            result = left.compareTo(right) < 0;
            break;
        }
        case "is-after": {
            const leftValue = get(c.left);
            const rightValue = get(c.right);
            if (isLoadingValue(leftValue)) return leftValue;
            if (isLoadingValue(rightValue)) return rightValue;
            const left = parseDateTime(leftValue);
            const right = parseDateTime(rightValue);
            if (left === undefined || right === undefined) return undefined;
            result = left.compareTo(right) > 0;
            break;
        }
        case "is-on-or-before": {
            const leftValue = get(c.left);
            const rightValue = get(c.right);
            if (isLoadingValue(leftValue)) return leftValue;
            if (isLoadingValue(rightValue)) return rightValue;
            const left = parseDateTime(leftValue);
            const right = parseDateTime(rightValue);
            if (left === undefined || right === undefined) return undefined;
            result = left.compareTo(right.endOfDay()) <= 0;
            break;
        }
        case "is-on-or-after": {
            const leftValue = get(c.left);
            const rightValue = get(c.right);
            if (isLoadingValue(leftValue)) return leftValue;
            if (isLoadingValue(rightValue)) return rightValue;
            const left = parseDateTime(leftValue);
            const right = parseDateTime(rightValue);
            if (left === undefined || right === undefined) return undefined;
            result = left.compareTo(right.startOfDay()) > 0;
            break;
        }
        case "is-within": {
            const leftValue = get(c.left);
            const rightValue = get(c.right);
            if (isLoadingValue(leftValue)) return leftValue;
            if (isLoadingValue(rightValue)) return rightValue;
            const left = parseDateTime(leftValue);
            const right = parseDateTime(rightValue);
            if (left === undefined || right === undefined) return undefined;
            result = left.compareTo(right.startOfDay()) >= 0 && left.compareTo(right.endOfDay()) <= 0;
            break;
        }
        case "matches-email-address":
            if (c.left === undefined || c.right === undefined) {
                result = false;
            } else {
                const leftValue = getNormalized(c.left);
                const rightValue = getNormalized(c.right);
                if (isLoadingValue(leftValue)) return leftValue;
                if (isLoadingValue(rightValue)) return rightValue;
                const left = makeEmailAddressSet(leftValue);
                const right = makeEmailAddressSet(rightValue);
                result = areSetsOverlapping(left, right);
            }
            break;
        case "is-truthy": {
            const value = getNormalized(c.value);
            if (isLoadingValue(value)) return value;
            result = isTruthy(value);
            break;
        }
        case "is-empty": {
            const value = getNormalized(c.value);
            if (isLoadingValue(value)) return value;
            result = !isNotEmpty(value);
            break;
        }
        default:
            return assertNever(c);
    }

    if (c.negate) {
        result = !result;
    }
    return result;
}
